import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { SentinelMetadata } from '../../../../store/Map/Sentinel/model';
import Constants from '../../../../constants';
import Analytics from '../../../../lib/user-analytics';

const moment = require('moment');

export interface DateRange {
    startDate: Date;
    endDate: Date;
    displayText: string;
}

export const last30Days: DateRange = {
    startDate: moment(new Date()).subtract(1, 'months').toDate(),
    endDate: new Date(),
    displayText: 'Last 30 days',
};

interface SentinelFilterProps {
    onSelectLayer: (sensor: SentinelMetadata) => void;
    layer: SentinelMetadata;
    dateRange: DateRange;
    onSelectDateRange: (dateRange: DateRange) => void;
}
const SentinelFilter = (props: SentinelFilterProps) => {
    const [sensor, setLayer] = useState<SentinelMetadata>(props.layer);
    const [dates, setDates] = useState<DateRange[]>([]);
    const [selectedDate, setSelectedDate] = useState<DateRange>(props.dateRange);
    const { onSelectDateRange, onSelectLayer } = props;

    const dateOptions = useCallback(() => {
        Analytics.Event('Satellite - Sentinel', 'Date filter changed');
        const previousMonth = moment(new Date()).subtract(1, 'months');
        const beginning = moment(new Date()).year(2016).month('January').date(1);
        const date = beginning;
        const dates: DateRange[] = [];
        while (date.isBefore(previousMonth)) {
            const endDate = date.clone();
            const startDate = date.clone().subtract(1, 'months');
            dates.push({
                startDate: startDate.toDate(),
                endDate: endDate.toDate(),
                displayText: startDate.format('MMMM YYYY'),
            });
            date.add(1, 'months');
        }
        return dates.reverse();
    }, []);

    useEffect(() => {
        setDates(dateOptions());
    }, [dateOptions]);

    const changeLayer = (layer: SentinelMetadata) => {
        setLayer(layer);
        onSelectLayer(layer);
    };

    const changeDateRange = (date: DateRange) => {
        setSelectedDate(date);
        onSelectDateRange(date);
    };

    const handleChangeLayer = (key: string) => {
        Analytics.Event('Satellite - Landsat', 'Changed layer', key);
        switch (key) {
            case 'TRUE_COLOR':
                changeLayer(Constants.OVERLAY_DATA.SENTINEL.TRUE_COLOR);
                break;
            case 'FALSE_COLOR':
                changeLayer(Constants.OVERLAY_DATA.SENTINEL.FALSE_COLOR);
                break;
            case 'GEOLOGY':
                changeLayer(Constants.OVERLAY_DATA.SENTINEL.GEOLOGY);
                break;
            case 'NDVI':
                changeLayer(Constants.OVERLAY_DATA.SENTINEL.NDVI);
                break;
            case 'SENTINEL_1_GRD':
            case 'SENTINEL_1_RADAR':
                changeLayer(Constants.OVERLAY_DATA.SENTINEL.SENTINEL_1_RADAR);
                break;
            default:
                throw new Error('No overlay key for: ' + key);
        }
    };

    return (
        <Container>
            <SelectIcon>
                <img src="/assets/sentinel-filter/layers.svg" />
            </SelectIcon>
            <Select value={sensor.key} onChange={(e) => handleChangeLayer(e.target.value)}>
                <Option value="TRUE_COLOR">True Color</Option>
                <Option value="FALSE_COLOR">False Color</Option>
                <Option value="GEOLOGY">Geology</Option>
                <Option value="NDVI">Vegetation Health</Option>
                <Option value="SENTINEL_1_RADAR">Radar</Option>
            </Select>

            <SelectIcon>
                <img src="/assets/sentinel-filter/calendar.svg" />
            </SelectIcon>
            <Select
                value={selectedDate.displayText}
                onChange={(e) => {
                    const displayText = e.target.value as string;
                    const value = dates.find((t) => t.displayText === displayText);
                    if (value) {
                        changeDateRange(value);
                    } else {
                        changeDateRange(last30Days);
                    }
                }}
            >
                <Option value={last30Days.displayText}>{last30Days.displayText}</Option>
                {dates.map((t, index) => {
                    return (
                        <Option key={index} value={t.displayText}>
                            {t.displayText}
                        </Option>
                    );
                })}
            </Select>
        </Container>
    );
};

export default SentinelFilter;

const Container = styled.div`
    pointer-events: all;
    user-select: none;
    min-height: 32px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    width: 100%;
    display: flex;
    flex-direction: row;

    @media only screen and (max-width: 600px) {
        min-height: auto;
        margin-bottom: 8px;
        border-bottom: none;
    }
`;

const Select = styled.select`
    background-color: #464646;
    width: 100%;
    background-position: 123px 1px;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    outline-width: 0;
    color: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-left: none;
    border-radius: 4px;
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
    margin: 4px 4px 0 4px;
    padding: 0 6px 0 5px;
    height: 22px;
    display: block;
    font-size: 1rem;
    cursor: pointer;

    background-image: url(/assets/arrows/down-arrow.svg);
    background-position: calc(100% - 20px) calc(50%), calc(100% - 15px) calc(50%), 100% 0;
    background-size: 10px 10px, 10px 10px, 2.5em 2.5em;
    background-repeat: no-repeat;

    &::-webkit-scrollbar-track {
        box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        background-color: #343a40;
    }

    &::-webkit-scrollbar {
        width: 8px;
        background-color: #343a40;
    }

    &::-webkit-scrollbar-thumb {
        box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
        background-color: #eed926;
        border-radius: 4px;
    }

    @media only screen and (min-width: 601px) and (max-width: 1024px) {
        pointer-events: auto;
        background-color: transparent;
        margin: 4px 4px 4px 0px;
        padding: 6px;
        height: auto;
        color: rgba(255, 255, 255, 0.87);

        background-image: url(/assets/arrows/down-arrow.svg);
        background-position: calc(100% - 4px);
        background-size: 15px 15px;
        background-repeat: no-repeat;
    }
`;

const Option = styled.option`
    color: rgba(255, 255, 255, 0.6);
`;

const SelectIcon = styled.div`
    img {
        height: 16px;
        margin-top: -6px;
        opacity: 0.6;
    }
    height: 22px;
    background-color: #464646;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    margin: 4px 16px 0 8px;
    padding: 0 6px 0 5px;
    border-right: none;
    background-repeat: no-repeat;
    background-size: 16px 16px;
    display: inline-block;
    margin-right: -10px;
    padding-bottom: 0px;

    @media only screen and (min-width: 601px) and (max-width: 1024px) {
        img {
            height: auto;
            margin-top: auto;
        }
        margin: 4px 0px 4px 4px;
        background-color: transparent;
        padding: 4px 0px 0px 5px;
        height: auto;
        border-radius: 0px;
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
    }
`;
